
import React, { useState, useEffect } from 'react';
import { X, ShieldCheck, Mail, ArrowLeft, Loader2, Save, FileSpreadsheet, CheckCircle2 } from 'lucide-react';
import { sendOTPToEmail } from '../services/dataService';

interface CsvUploadOtpModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => Promise<void>;
  email: string;
  transactionCount: number;
  theme: 'dark' | 'light';
}

const generateAlphanumericCode = (length: number = 6) => {
  const letters = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const numbers = '23456789';
  const allChars = letters + numbers;
  let result = '';
  result += letters.charAt(Math.floor(Math.random() * letters.length));
  result += numbers.charAt(Math.floor(Math.random() * numbers.length));
  for (let i = 0; i < length - 2; i++) {
    result += allChars.charAt(Math.floor(Math.random() * allChars.length));
  }
  return result.split('').sort(() => 0.5 - Math.random()).join('');
};

const CsvUploadOtpModal: React.FC<CsvUploadOtpModalProps> = ({ isOpen, onClose, onSuccess, email, transactionCount, theme }) => {
  const [step, setStep] = useState<'confirm' | 'otp' | 'uploading'>('confirm');
  const [otpInput, setOtpInput] = useState('');
  const [generatedOtp, setGeneratedOtp] = useState('');
  const [otpCreatedAt, setOtpCreatedAt] = useState<number | null>(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
        setStep('confirm');
        setOtpInput('');
        setError(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (step === 'otp' && timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (step === 'otp' && timeLeft === 0) {
      setError("Código expirado. Genere uno nuevo.");
    }
  }, [step, timeLeft]);

  if (!isOpen) return null;

  const handleSendCode = async () => {
    setIsLoading(true);
    setError(null);
    const otp = generateAlphanumericCode(6);
    try {
        await sendOTPToEmail(email, otp); // Fire and forget logic sometimes, but here we wait to ensure net
        setGeneratedOtp(otp);
        setOtpCreatedAt(Date.now());
        setTimeLeft(300);
        setStep('otp');
    } catch (e) {
        setError("Error al enviar el código. Intente de nuevo.");
    } finally {
        setIsLoading(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otpCreatedAt) return;
    
    if (otpInput.toUpperCase().trim() === generatedOtp.toUpperCase()) {
        setStep('uploading');
        try {
            await onSuccess();
            onClose();
        } catch (e) {
            setStep('otp');
            setError("Error al guardar en base de datos.");
        }
    } else {
        setError("Código incorrecto.");
    }
  };

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const themeClasses = {
    overlay: "fixed inset-0 bg-black/80 backdrop-blur-sm z-[200] flex items-center justify-center p-4 animate-in fade-in duration-300",
    container: `${theme === 'dark' ? 'bg-[#1a1c22] border-[#2d3139]' : 'bg-white border-slate-200'} border w-full max-w-sm rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200`,
    textMain: theme === 'dark' ? 'text-white' : 'text-slate-900',
    textMuted: theme === 'dark' ? 'text-slate-400' : 'text-slate-500',
    inputBg: theme === 'dark' ? 'bg-black/40 border-white/10 text-white' : 'bg-slate-50 border-slate-200 text-slate-800'
  };

  return (
    <div className={themeClasses.overlay}>
      <div className={themeClasses.container}>
        <div className="p-8">
            {step === 'confirm' && (
                <div className="text-center space-y-6">
                    <div className="w-16 h-16 bg-indigo-600 rounded-full flex items-center justify-center mx-auto shadow-xl shadow-indigo-600/20">
                        <FileSpreadsheet size={32} className="text-white" />
                    </div>
                    <div>
                        <h2 className={`text-xl font-black uppercase tracking-tight ${themeClasses.textMain}`}>Registrar CSV</h2>
                        <p className={`text-xs mt-2 ${themeClasses.textMuted}`}>
                            Estás a punto de guardar <strong className="text-indigo-500">{transactionCount}</strong> transacciones en la base de datos permanente.
                        </p>
                    </div>
                    <div className={`p-4 rounded-xl border border-dashed ${theme === 'dark' ? 'border-white/10 bg-white/5' : 'border-slate-300 bg-slate-50'}`}>
                        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Requiere Verificación</p>
                        <div className="flex items-center justify-center space-x-2 text-xs font-bold">
                            <Mail size={14} className="text-indigo-500" />
                            <span className={themeClasses.textMain}>{email}</span>
                        </div>
                    </div>
                    <button 
                        onClick={handleSendCode} 
                        disabled={isLoading}
                        className="w-full py-4 bg-indigo-600 hover:bg-indigo-500 text-white rounded-2xl font-black uppercase text-[10px] tracking-widest transition-all shadow-lg shadow-indigo-600/20 flex items-center justify-center space-x-2"
                    >
                        {isLoading ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
                        <span>{isLoading ? 'Enviando...' : 'Enviar Código de Seguridad'}</span>
                    </button>
                    <button onClick={onClose} className="text-xs font-bold text-slate-500 hover:text-white transition-colors">Cancelar</button>
                </div>
            )}

            {step === 'otp' && (
                <form onSubmit={handleVerify} className="space-y-6 text-center animate-in slide-in-from-right-4 duration-300">
                    <button type="button" onClick={() => { setStep('confirm'); setError(null); }} className="flex items-center space-x-2 text-[10px] font-black text-slate-500 uppercase hover:text-indigo-500 transition-colors mb-2">
                        <ArrowLeft size={14} /> <span>Volver</span>
                    </button>
                    <div className="space-y-2">
                        <ShieldCheck size={40} className="text-emerald-500 mx-auto" />
                        <h2 className={`text-xl font-black uppercase ${themeClasses.textMain}`}>Código Enviado</h2>
                        <div className={`text-[10px] font-black uppercase px-3 py-1 rounded-full inline-block ${timeLeft < 60 ? 'bg-rose-500/10 text-rose-500 animate-pulse' : 'bg-indigo-500/10 text-indigo-500'}`}>
                            Expira en: {formatTime(timeLeft)}
                        </div>
                    </div>
                    
                    <input 
                        type="text" 
                        value={otpInput} 
                        onChange={e => setOtpInput(e.target.value.toUpperCase())} 
                        className={`w-full py-4 text-center text-2xl font-black tracking-[0.5em] outline-none rounded-xl border focus:border-indigo-500 font-mono transition-all uppercase ${themeClasses.inputBg}`} 
                        placeholder="------" 
                        maxLength={6} 
                        required 
                    />
                    
                    {error && <p className="text-rose-500 text-[10px] font-black uppercase bg-rose-500/10 py-2 rounded-lg">{error}</p>}
                    
                    <button type="submit" disabled={timeLeft === 0} className="w-full py-4 bg-emerald-600 hover:bg-emerald-500 text-white rounded-2xl font-black uppercase text-[10px] tracking-widest transition-all shadow-lg active:scale-95 disabled:opacity-30">
                        Verificar y Guardar
                    </button>
                </form>
            )}

            {step === 'uploading' && (
                <div className="text-center py-8 space-y-6 animate-in zoom-in-95 duration-300">
                    <div className="relative mx-auto w-20 h-20">
                        <div className="absolute inset-0 border-4 border-indigo-500/20 rounded-full"></div>
                        <div className="absolute inset-0 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
                        <Save size={24} className="absolute inset-0 m-auto text-indigo-500 animate-pulse" />
                    </div>
                    <div>
                        <h2 className={`text-lg font-black uppercase tracking-tight ${themeClasses.textMain}`}>Sincronizando</h2>
                        <p className={`text-xs mt-2 ${themeClasses.textMuted}`}>Guardando transacciones en la nube...</p>
                    </div>
                </div>
            )}
        </div>
      </div>
    </div>
  );
};

export default CsvUploadOtpModal;
